import type {
  PublicTrialRegistrationError,
  PublicTrialRegistrationErrorTarget,
} from "./publicTrialRegistrationError.types";

export interface PublicTrialRegistrationErrorMessages {
  conflict: string;
  turnstile: string;
  validation: string;
  rateLimited: string;
  unavailable: string;
  unknown: string;
}

const TERMINAL_PUBLIC_TRIAL_ERROR_CODES = new Set([
  "PUBLIC_TRIAL_REGISTRATION_CONFLICT",
  "PUBLIC_TRIAL_PROVISIONING_FAILED",
]);

export type PublicTrialRegistrationFailureType =
  | "conflict"
  | "turnstile"
  | "validation"
  | "rate_limited"
  | "unavailable"
  | "unknown";

function getErrorContext(error: unknown) {
  const response = (error as PublicTrialRegistrationError | undefined)?.response;

  return {
    status: response?.status,
    codes:
      response?.data?.errors?.map((entry) => entry.code).filter((code): code is string => typeof code === "string") ??
      [],
    detail:
      response?.data?.errors
        ?.map((entry) => entry.detail)
        .filter(Boolean)
        .join(" ")
        .toLowerCase() ?? "",
  };
}

export function getPublicTrialRegistrationErrorTarget(error: unknown): PublicTrialRegistrationErrorTarget {
  const { detail } = getErrorContext(error);

  if (detail.includes("turnstile")) return "turnstile";
  return "toast";
}

export function getPublicTrialRegistrationErrorMessage(
  error: unknown,
  messages: PublicTrialRegistrationErrorMessages,
): string {
  const { codes, detail, status } = getErrorContext(error);
  if (status === 409 || codes.some((code) => TERMINAL_PUBLIC_TRIAL_ERROR_CODES.has(code))) return messages.conflict;
  if (detail.includes("turnstile")) return messages.turnstile;
  if (status === 400) return messages.validation;
  if (status === 429) return messages.rateLimited;
  if (status === 503) return messages.unavailable;
  if (error instanceof Error && error.message) return error.message;
  return messages.unknown;
}

export function getPublicTrialRegistrationFailureProperties(error: unknown): {
  failure_type: PublicTrialRegistrationFailureType;
  status_code?: number;
} {
  const { detail, status } = getErrorContext(error);

  let failureType: PublicTrialRegistrationFailureType = "unknown";
  if (status === 409) failureType = "conflict";
  else if (detail.includes("turnstile")) failureType = "turnstile";
  else if (status === 400) failureType = "validation";
  else if (status === 429) failureType = "rate_limited";
  else if (status === 503) failureType = "unavailable";

  return {
    failure_type: failureType,
    ...(status === undefined ? {} : { status_code: status }),
  };
}
